// components/Legal/perspectivesModel.js
// Pure, testable view-model for the Perspectives tab.

export const PERSPECTIVE_ROLES = ['defense', 'prosecutor', 'judge'];

export const ROLE_META = {
  defense: {
    title: 'Defense Attorney',
    icon: '🛡️',
    subtitle: 'Senior Defense Counsel',
    className: 'perspective-defense',
  },
  prosecutor: {
    title: 'Prosecutor',
    icon: '⚖️',
    subtitle: 'Senior Public Prosecutor',
    className: 'perspective-prosecutor',
  },
  judge: {
    title: 'Judge',
    icon: '👁️',
    subtitle: 'Presiding Judge · Neutral Arbiter',
    className: 'perspective-judge',
  },
};

export const PERSPECTIVE_SECTIONS = [
  { key: 'observations', label: 'Observations', icon: '🔍' },
  { key: 'consistency', label: 'Consistency Checks', icon: '🧩' },
  { key: 'missing', label: 'Missing Information', icon: '🕳️' },
  { key: 'issues', label: 'Data & Procedural Issues', icon: '⚠️' },
  { key: 'recommendations', label: 'Neutral Recommendations', icon: '💡' },
];

export const getRoleMeta = (role) =>
  ROLE_META[role] || {
    title: role,
    icon: '👁️',
    subtitle: '',
    className: 'perspective-unknown',
  };

export const getSectionItems = (perspective, key) => {
  const value = perspective?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item) => typeof item === 'string' && item.trim() !== '');
};

export const hasAnyContent = (perspective) =>
  Boolean(perspective) &&
  PERSPECTIVE_SECTIONS.some((section) => getSectionItems(perspective, section.key).length > 0);

// Sections that represent "data flags" rather than plain observations.
export const FLAG_SECTION_KEYS = ['consistency', 'missing', 'issues'];

export const getSectionCount = (perspective, key) => getSectionItems(perspective, key).length;

export const getFlagCounts = (perspective) =>
  PERSPECTIVE_SECTIONS.reduce((acc, section) => {
    acc[section.key] = getSectionCount(perspective, section.key);
    return acc;
  }, {});

export const getTotalFlags = (perspective) =>
  FLAG_SECTION_KEYS.reduce((sum, key) => sum + getSectionCount(perspective, key), 0);

/**
 * Merge recommendations across all roles into one de-duplicated,
 * role-tagged list (stable order: defense → prosecutor → judge).
 */
export const aggregateRecommendations = (perspectives) => {
  const seen = new Set();
  const result = [];
  for (const role of PERSPECTIVE_ROLES) {
    const items = getSectionItems(perspectives?.[role], 'recommendations');
    for (const item of items) {
      const key = item.trim().toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ role, text: item });
      }
    }
  }
  return result;
};
